import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import Text from 'common/ui/Text';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Close from '@mui/icons-material/Close';
import { ApolloCache, useMutation } from '@apollo/client';
import {
  GET_TOOL_PREFERENCES,
  SET_WWWC_DRAG_DIRECTION,
  TOGGLE_MANUAL_LOCALIZER_LINES,
  TOGGLE_STACKED_SCROLLING,
  UPDATE_MOUSE_SCROLL_DIRECTION,
  UPDATE_STACKED_MODALITIES,
  TOGGLE_ABSOLUTE_SCROLL,
} from 'modules/Apollo/queries';
import { Stack, Spacer } from 'common/ui/Layout';
import { useCurrentUser } from 'hooks/useCurrentUser';
import type {
  GetToolPreferencesQuery,
  GetToolPreferencesQueryVariables,
  GetMeQuery,
  ToggleStackedScrollingMutation,
  ToggleManualLocalizerLinesMutation,
  UpdateMouseScrollDirectionMutation,
  UpdateMouseScrollDirectionMutationVariables,
  ToggleAbsoluteScrollMutation,
} from 'generated/graphql';
import { useIsWwwcInverted, useIsScrollInverted } from 'hooks/useIsScrollInverted';
import { useBaseViewerData } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { SettingsCard, SettingsCardHeader, SettingsSeparator } from './SettingsLayoutComponents';
import type { DocumentNode } from 'graphql';
import { VirtualMonitorSplitSettings } from './VirtualMonitorSplitSettings';
import { EraseAllToolScopeSettings } from './EraseAllToolScopeSettings';
import { SkipSeriesSettings } from './SkipSeriesSettings';
import { purgePrecache } from 'modules/Apollo/links/precacheLink';
import { notifyUserSettingsUpdate } from 'hooks/notifyUserSettingsUpdate';
import Tooltip from 'common/ui/Tooltip';
import Info from '@mui/icons-material/Info';
import { ThumbnailSizeSettings } from './ThumbnailSizeSettings';
import { StackedScrollingSettings } from './StackedScrollingSettings';
import { StartingImageSettings } from './StartingImageSettings';
import { telemetryContextFor, WithApolloContext } from 'modules/Apollo/types';

type ViewerSettingsTabProps = {
  onClose: () => void;
  setError: Dispatch<SetStateAction<string | null>>;
};

export const ViewerSettingsTab = ({
  onClose,
  setError,
}: ViewerSettingsTabProps): React.ReactElement => {
  const {
    isScrollInverted,
    handleUpdateMouseScrollDirection,
    isWwwcInverted,
    handleInvertWwwcChange,
  } = useMouseSettingsToggles({ setError, context: APOLLO_QUERY_CONTEXT });

  const [stackedScrollingEnabled, handleStackedScrollingChange] = useStackedScrollingToggle({
    setError,
    context: APOLLO_QUERY_CONTEXT,
  });

  const [absoluteScrollingEnabled, handleAbsoluteScrollingChange] = useAbsoluteScrollingToggle({
    setError,
    context: APOLLO_QUERY_CONTEXT,
  });

  const [manualLocalizerLinesEnabled, handleManualLocalizerLinesChange] =
    useManualLocalizerLinesToggle({ setError, context: APOLLO_QUERY_CONTEXT });

  return (
    <SettingsCard>
      <SettingsCardHeader>
        <Stack alignY="center">
          <Text variant="display2">Viewer Settings</Text>
          <Spacer />
          <IconButton onClick={onClose} size="large">
            <Close />
          </IconButton>
        </Stack>
      </SettingsCardHeader>
      <Stack
        vertical
        css={`
          padding: 1.5rem 3rem;
          overflow: auto;
          min-height: 0;
        `}
        space="xsmall"
      >
        <Text variant="display1" display="block" gutterBottom="small">
          General
        </Text>
        <VirtualMonitorSplitSettings />
        <SkipSeriesSettings />
        <Stack alignY="center">
          <Text>Show localizer lines for manually-linked studies</Text>
          <Spacer />
          <Switch
            checked={manualLocalizerLinesEnabled}
            onChange={handleManualLocalizerLinesChange}
          />
        </Stack>
        <EraseAllToolScopeSettings />
        <ThumbnailSizeSettings />
        <StartingImageSettings />
        <SettingsSeparator />
        <Text variant="display1" display="block" gutterBottom="small">
          Mouse
        </Text>
        <Stack alignY="center">
          <Text>Invert mouse wheel scroll direction</Text>
          <Spacer />
          <Switch
            checked={isScrollInverted}
            onChange={handleUpdateMouseScrollDirection}
            slotProps={{ input: { 'aria-label': 'Invert mouse wheel scroll direction' } }}
          />
        </Stack>
        <Stack alignY="center">
          <Text>Invert Window Width/Level drag direction</Text>
          <Spacer />
          <Switch
            checked={isWwwcInverted}
            onChange={handleInvertWwwcChange}
            slotProps={{ input: { 'aria-label': 'Invert Window Width/Level drag direction' } }}
          />
        </Stack>
        <Stack alignY="center">
          <Text>Absolute scrolling</Text>
          <Tooltip
            content="A sync setting to correct for a common data error where series that share a frame of reference are given opposite directions"
            placement="top"
            backgroundColor="gray"
          >
            <span
              style={{ position: 'relative', top: '2px', cursor: 'pointer', marginLeft: '4px' }}
            >
              <Info css="width: 14px; height: 14px" />
            </span>
          </Tooltip>
          <Spacer />
          <Switch
            checked={absoluteScrollingEnabled}
            onChange={handleAbsoluteScrollingChange}
            data-testid="absolute-scrolling"
            slotProps={{ input: { 'aria-label': 'Absolute scrolling' } }}
          />
        </Stack>
        <Stack alignY="center">
          <Text>Stacked scrolling</Text>
          <Spacer />
          <Switch
            checked={stackedScrollingEnabled}
            onChange={handleStackedScrollingChange}
            data-testid="stacked-scrolling"
            slotProps={{ input: { 'aria-label': 'Stacked scrolling' } }}
          />
        </Stack>
        <StackedScrollingSettings setError={setError}></StackedScrollingSettings>
      </Stack>
    </SettingsCard>
  );
};

type ToggleProps = WithApolloContext & {
  setError: Dispatch<SetStateAction<string | null>>;
};

const useMouseSettingsToggles = ({ setError, context }: ToggleProps) => {
  const [setWwwcDragDirection] = useMutation(SET_WWWC_DRAG_DIRECTION, {
    context,
    refetchQueries: [{ query: GET_TOOL_PREFERENCES }],
    update(cache: ApolloCache<unknown>, response, { variables }) {
      const { toolPreferences } =
        cache.readQuery<GetToolPreferencesQuery, GetToolPreferencesQueryVariables>({
          query: GET_TOOL_PREFERENCES,
        }) ?? {};

      if (toolPreferences == null) {
        return;
      }
      cache.writeQuery({
        query: GET_TOOL_PREFERENCES,
        data: {
          toolPreferences: {
            ...toolPreferences,
            wwwc: {
              ...toolPreferences.wwwc,
              invertDragDirection: variables?.value,
            },
          },
        },
      });
    },
  });

  const isWwwcInverted = useIsWwwcInverted({ context });

  const handleInvertWwwcChange = () => {
    setWwwcDragDirection({
      variables: { value: !isWwwcInverted },
      optimisticResponse: {
        __typename: 'Mutation',
        setWwwcDragDirection: {
          invertDragDirection: !isWwwcInverted,
        },
      },
    });

    notifyUserSettingsUpdate();
  };

  const [updateMouseScrollDirection] = useMutation<
    UpdateMouseScrollDirectionMutation,
    UpdateMouseScrollDirectionMutationVariables
  >(UPDATE_MOUSE_SCROLL_DIRECTION, {
    update(cache: ApolloCache<unknown>, response, { variables = {} }) {
      const toolPreferences = cache.readQuery<
        GetToolPreferencesQuery,
        GetToolPreferencesQueryVariables
      >({
        query: GET_TOOL_PREFERENCES,
      })?.toolPreferences;

      cache.writeQuery({
        query: GET_TOOL_PREFERENCES,
        data: {
          toolPreferences: {
            ...toolPreferences,
            invertScroll: variables.invertScroll,
          },
        },
      });
    },
    context,
  });

  const isScrollInverted = useIsScrollInverted({ context });
  const handleUpdateMouseScrollDirection = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateMouseScrollDirection({
      variables: {
        invertScroll: event.target.checked,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateMouseScrollDirection: {
          __typename: 'ToolPreferences',
          invertScroll: event.target.checked,
        },
      },
    });

    notifyUserSettingsUpdate();
  };

  return {
    isScrollInverted,
    handleUpdateMouseScrollDirection,
    isWwwcInverted,
    handleInvertWwwcChange,
  };
};

const useStackedScrollingToggle = ({
  setError,
  context,
}: { setError: Dispatch<SetStateAction<string | null>> } & WithApolloContext) => {
  const { refetch } = useBaseViewerData({ context });
  const handleChange = useCallback(async () => {
    await purgePrecache();
    refetch();
  }, [refetch]);
  return useUserPreferenceValue<ToggleStackedScrollingMutation, boolean>({
    query: TOGGLE_STACKED_SCROLLING,
    preferenceKey: 'stackInstancesByType',
    onChange: handleChange,
    setError,
    initValue: false,
    context,
  });
};

const useAbsoluteScrollingToggle = ({ setError, context }: ToggleProps) => {
  const getStateFromUser = useCallback(
    (user: NonNullable<GetMeQuery['me']>) => user.viewerSettings.absoluteScroll ?? true,
    []
  );
  return useUserPreferenceValue<ToggleAbsoluteScrollMutation, boolean>({
    query: TOGGLE_ABSOLUTE_SCROLL,
    preferenceKey: 'absoluteScroll',
    getStateFromUser,
    setError,
    initValue: false,
    context,
  });
};

export const useStackedScrollingModalities = ({ setError, context }: ToggleProps) => {
  const { refetch } = useBaseViewerData({ context });
  const handleChange = useCallback(async () => {
    await purgePrecache();
    refetch();
  }, [refetch]);
  return useUserPreferenceValue<ToggleStackedScrollingMutation, ReadonlyArray<string>>({
    query: UPDATE_STACKED_MODALITIES,
    preferenceKey: 'stackedModalities',
    onChange: handleChange,
    setError,
    initValue: [],
    context,
  });
};

const useManualLocalizerLinesToggle = ({ setError, context }: ToggleProps) => {
  const getStateFromUser = useCallback(
    (user: NonNullable<GetMeQuery['me']>) => user.viewerSettings.showManualLocalizerLines,
    []
  );
  return useUserPreferenceValue<ToggleManualLocalizerLinesMutation, boolean>({
    query: TOGGLE_MANUAL_LOCALIZER_LINES,
    preferenceKey: 'showManualLocalizerLines',
    getStateFromUser,
    setError,
    initValue: false,
    context,
  });
};

type UseUserPreferenceProps<T> = WithApolloContext & {
  query: DocumentNode;
  preferenceKey: keyof GetMeQuery['me'] | keyof GetMeQuery['me']['viewerSettings'];
  getStateFromUser?: (user: NonNullable<GetMeQuery['me']>) => T;
  onChange?: () => unknown;
  setError: Dispatch<SetStateAction<string | null>>;
  initValue: T;
};

const useUserPreferenceValue = <Mutation extends unknown, T extends unknown>({
  query,
  preferenceKey,
  getStateFromUser,
  onChange,
  setError,
  initValue,
  context,
}: UseUserPreferenceProps<T>): [T, (_: unknown, value: T) => Promise<void>] => {
  const { data, refetch: refetchUser } = useCurrentUser({ context });
  const me = data?.me;
  const [setPreference] = useMutation<
    Mutation,
    {
      [preferencesKey: string]: T;
    }
  >(query, {
    context,
  });

  const [enabled, setEnabled] = useState(initValue);

  useEffect(() => {
    if (me) {
      // @ts-expect-error [EN-10263] - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly __typename?: "User" | undefined; readonly id: string; readonly firstName: string; readonly lastName: string; readonly type: string; readonly email: string; readonly physicianId?: string | null | undefined; ... 14 more ...; readonly defaultAutoloadTemplate?: { ...; } | ... 1 more ... | undefined; }'.
      setEnabled(getStateFromUser?.(me) ?? me[preferenceKey]);
    }
  }, [me, getStateFromUser, preferenceKey]);

  const sendUpdate = async (payload: T) => {
    setError(null);

    try {
      await setPreference({
        variables: {
          [preferenceKey]: payload,
        },
      });
    } catch (e: any) {
      setError('An error occurred. Please try again.');
    }
    refetchUser();
    onChange?.();
  };

  const handleChange = async (_: unknown, checked: T) => {
    setEnabled(checked);
    await sendUpdate(checked);

    notifyUserSettingsUpdate();
  };

  return [enabled, handleChange];
};

ViewerSettingsTab.displayName = 'ViewerSettingsTab';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ViewerSettingsTab);
